import React, { useEffect } from "react";
import { Link } from "react-router-dom";

/**
 * StackMyDeck – Terms of Use component
 * Rendered at /terms or similar route
 */
const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <h1 className="text-3xl font-bold mb-4">StackMyDeck Terms of Use</h1>
      <p className="mb-6">Last Updated: July&nbsp;19, 2025</p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">1. Eligibility</h2>
      <p className="mb-4">
        You must be <strong>at least 18 years old</strong> and have the legal capacity to enter into
        these Terms to use this Site.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">2. Use of the Site</h2>
      <h3 className="text-xl font-medium mt-6 mb-1">2.1 License</h3>
      <p className="mb-4">
        Stack My Deck, LLC ("<strong>Company</strong>", "<strong>we</strong>") grants you a
        limited, non‑exclusive, non‑transferable, revocable license to access and use
        <strong>stackmydeck.com</strong> (the "<strong>Site</strong>") for your personal,
        non‑commercial use, subject to these Terms.
      </p>
      <h3 className="text-xl font-medium mt-6 mb-1">2.2 Restrictions</h3>
      <ul className="list-disc pl-6 space-y-2 mb-4">
        <li>Sell, lease, sublicense, distribute, or otherwise commercially exploit the Site;</li>
        <li>Copy, reproduce, modify, create derivative works of, reverse engineer, or decompile any part of the Site;</li>
        <li>Use the Site to build a competing product or service;</li>
        <li>
          Remove or alter any copyright, trademark, or proprietary notice displayed on the Site or in its content.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-2">3. Intellectual Property</h2>
      <p className="mb-4">
        All content on the Site—including text, graphics, logos, and code—is the property of
        Stack My Deck, LLC or its licensors. No rights are granted to you other than the
        limited license described in Section 2.1.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">4. User Content</h2>
      <p className="mb-4">
        You are solely responsible for any content you submit ("<strong>User Content</strong>").
        We reserve the right to remove or disable User Content at our discretion. By posting,
        you grant us a worldwide, royalty‑free, perpetual license to use, display, and
        distribute that content in connection with the Site.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">5. Third‑Party Links &amp; Ads</h2>
      <p className="mb-4">
        The Site may contain links to, or display ads from, third‑party services ("Third‑Party
        Content"). We do not control or endorse Third‑Party Content and are not responsible
        for it. Your use of Third‑Party Content is entirely at your own risk.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">6. Cookies</h2>
      <p className="mb-4">
        We use cookies to personalize content and analyze traffic. By using the Site, you
        consent to our use of cookies as described in our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">7. Disclaimers</h2>
      <p className="mb-4">
        The Site is provided <em>"as is"</em> and <em>"as available"</em>. We disclaim all warranties—express or
        implied—including merchantability, fitness for a particular purpose, non‑infringement,
        and accuracy. We do not warrant that the Site will be uninterrupted, secure, or
        error‑free.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">8. Limitation of Liability</h2>
      <p className="mb-4">
        To the maximum extent allowed by law, neither we nor our suppliers shall be liable for
        any indirect, incidental, consequential, or special damages. Our total liability will
        never exceed <strong>$50 USD</strong>.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">9. Termination</h2>
      <p className="mb-4">
        We may suspend or terminate your access to the Site at any time for any reason. Upon
        termination, your right to use the Site ends immediately, and we may delete your User
        Content.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">10. Dispute Resolution &amp; Arbitration</h2>
      <p className="mb-4">
        <strong>Mandatory binding arbitration.</strong> All disputes arising out of these Terms
        will be resolved individually through binding arbitration under the rules of the
        American Arbitration Association (AAA), unless resolved informally or in small
        claims court.
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-4">
        <li>You waive any right to a jury trial or to participate in class actions.</li>
        <li>Arbitration will occur within 100 miles of your residence (or virtually if you live outside the U.S.).</li>
        <li>The arbitrator may award monetary or injunctive relief as permitted by law.</li>
        <li>Claims of defamation, fraud, or IP infringement may proceed in court.</li>
        <li>For any litigation allowed, venue and jurisdiction lie in <strong>Orange County, FL</strong>.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-2">11. Copyright Policy</h2>
      <p className="mb-4">
        If you believe content on the Site infringes your copyright, please send a DMCA notice
        to <a href="mailto:webmaster@stackmydeck.com">webmaster@stackmydeck.com</a> with the
        information required by 17 U.S.C. §512(c)(3).
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">12. Modifications to These Terms</h2>
      <p className="mb-4">
        We may update these Terms from time to time. Material changes will be announced via
        email (if you have provided one) or by posting a notice on the Site. Continued use
        after changes become effective constitutes acceptance of the new Terms.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">13. Electronic Communications</h2>
      <p className="mb-4">
        By using the Site, you consent to receive communications from us electronically and
        agree that electronic agreements and notices satisfy any legal requirements.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-2">14. General Provisions</h2>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>These Terms constitute the entire agreement between you and Stack My Deck, LLC.</li>
        <li>If any provision is held unenforceable, the remaining provisions remain in effect.</li>
        <li>We may assign these Terms; you may not without our written consent.</li>
        <li>Section headings are for convenience only and have no legal effect.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-2">15. Contact Information</h2>
      <p className="mb-1">Stack My Deck, LLC</p>
      <p className="mb-1">Orlando, FL 32835</p>
      <p className="mb-8">
        Email:&nbsp;
        <a className="text-blue-600 underline" href="mailto:webmaster@stackmydeck.com">
          webmaster@stackmydeck.com
        </a>
      </p>

      <p className="text-sm text-gray-500">
        © {new Date().getFullYear()} Stack My Deck, LLC. All rights reserved.
      </p>
    </>
  );
};

export default TermsOfUse;
